import { useAuth } from "../auth/AuthContext";
import { useAsync } from "./useAsync";
import { api } from "../api/client";

export type TeamRole = "coach" | "parent";

/**
 * Centralized role logic. Two independent axes (confirmed product model):
 *  - Platform Admin: the Cognito `admin` group (ops/you). From the token.
 *  - Per-team role: "coach" (team admin) or "parent" (member). From memberships.
 * A user can be a Coach on one team and a Parent on another at the same time —
 * no re-login needed. "Coach" is the UI term for a team admin.
 */
export function useRoles() {
  const { isAdmin: isPlatformAdmin } = useAuth();
  const me = useAsync(() => api.getMe(), []);
  const teams = me.data?.teams ?? [];

  const roleOn = (teamId?: string): TeamRole | undefined => {
    if (!teamId) return undefined;
    const t = teams.find((x) => x.teamId === teamId);
    if (!t) return undefined;
    return t.role === "admin" ? "coach" : "parent";
  };
  const isCoachOf = (teamId?: string) => roleOn(teamId) === "coach";
  const coachTeamIds = teams
    .filter((t) => t.role === "admin")
    .map((t) => t.teamId);

  return {
    loading: me.loading,
    isPlatformAdmin,
    teams,
    roleOn,
    isCoachOf,
    /** Is the user a coach of at least one team? */
    anyCoach: coachTeamIds.length > 0,
    coachTeamIds,
  };
}

/** Human label for a team role. */
export function roleLabel(role?: TeamRole): string {
  return role === "coach" ? "Coach" : role === "parent" ? "Parent" : "";
}
